import React, { Fragment, useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import PurchaseEdit from "./PurchaseEdit";
import { useTranslation } from "react-i18next";
import useDelete from "../../../../api/useDelete";
import { useNavigate } from "react-router-dom";

const PurchaseDrugList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // --- Filters & State ---
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPurchases, setTotalPurchases] = useState(0);

  // --- Delete Hooks ---
  const {
    handleDelete: handleDeletePurchase,
    ConfirmDialog: ConfirmDeletePurchaseDialog,
  } = useDelete("purchases", token);

  const {
    handleDelete: handleDeletePurchaseItem,
    DeleteMessageComponent: DeletePurchaseItemMessage,
    ConfirmDialog: ConfirmDeletePurchaseItemDialog,
  } = useDelete("purchase-items", token);

  // --- Query string builder with filters ---
  const buildQueryString = () => {
    let params = [];
    if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
    if (filterDate) params.push(`date=${filterDate}`);
    params.push(`page=${page}`);
    params.push(`page_size=${rowsPerPage}`);
    return params.join("&");
  };

  // --- Fetch Purchases ---
  const { data, isLoading, isError, isSuccess, error, refetch } =
    useFetchObjects(
      ["purchases", searchQuery, filterDate, page, rowsPerPage],
      `purchases/?${buildQueryString()}`,
      token
    );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line
  }, [searchQuery, filterDate, page, rowsPerPage]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalPurchases(data.count);
    }
  }, [data]);

  // --- Dialog State ---
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  // --- UI Handlers ---
  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };
  const handleEditOpen = (purchase) => {
    setSelectedPurchase(purchase);
    setEditDialogOpen(true);
  };
  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedPurchase(null);
  };
  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };
  const handleClearDate = () => setFilterDate("");

  // --- Approval Status Display ---
  const getApprovalStatusLabel = (status) => {
    if (status === 1) return t("pharmacy.pending");
    if (status === 2) return t("pharmacy.approved");
    if (status === 3) return t("pharmacy.rejected");
    return t("pharmacy.unknownStatus");
  };
  const getApprovalStatusColor = (status) => {
    if (status === 1) return "warning";
    if (status === 2) return "success";
    if (status === 3) return "error";
    return "default";
  };

  // --- Render ---
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
      >
        <Typography variant="h6">{t("pharmacy.loading")}</Typography>
      </Box>
    );
  }
  if (isError) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6" color="error">
          {error.message || t("pharmacy.errorLoading")}
        </Typography>
      </Box>
    );
  }

  return (
    <Fragment>
      {/* Top Bar */}
      <Box
        my={4}
        display="flex"
        flexDirection={isSmallScreen ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isSmallScreen ? "stretch" : "center"}
        mb={3}
        gap={isSmallScreen ? 2 : 0}
        width="100%"
      >
        <Typography variant="h5" fontWeight="bold" color="primary" mb={2}>
          {t("pharmacy.purchaseList")}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/pharmacy/purchase-drug/add")}
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            paddingX: 3,
            paddingY: isSmallScreen ? 1 : 1.5,
            alignSelf: isSmallScreen ? "flex-end" : "center",
            borderRadius: "8px",
            boxShadow: 3,
            transition: "all 0.3s ease",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: 6,
              transform: "translateY(-2px)",
            },
          }}
        >
          {t("pharmacy.addNew")}
        </Button>
      </Box>

      {/* --- Filters Bar --- */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={isSmallScreen ? "stretch" : "center"}
        mb={2}
      >
        {/* Search */}
        <TextField
          variant="outlined"
          size="small"
          placeholder={t("pharmacy.search")}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            width: isSmallScreen ? "100%" : 250,
          }}
        />
        {/* Date Filter */}
        <TextField
          variant="outlined"
          size="small"
          type="date"
          label={t("pharmacy.filterByDate", "Filter By Date")}
          InputLabelProps={{ shrink: true }}
          value={filterDate}
          onChange={(e) => {
            setFilterDate(e.target.value);
            setPage(1);
          }}
          sx={{ width: isSmallScreen ? "100%" : 170 }}
        />

        {filterDate && (
          <Button
            onClick={handleClearDate}
            size="small"
            variant="outlined"
            color="secondary"
            sx={{ minWidth: 90, mt: isSmallScreen ? 1 : 0 }}
          >
            {t("pharmacy.clearDate", "Clear Date")}
          </Button>
        )}
      </Stack>

      {/* Purchases List */}
      <Grid container spacing={3}>
        {isSuccess && data?.results?.length > 0 ? (
          data.results.map((purchase) => {
            const status = purchase.is_approved;
            return (
              <Grid item xs={12} md={6} key={purchase.id}>
                <Paper
                  elevation={status === 2 ? 2 : 4}
                  sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: "14px",
                    backgroundColor:
                      status === 2
                        ? "#f9f9f9"
                        : status === 3
                        ? "#ffebee"
                        : "#fffde7",
                    borderLeft:
                      status === 3
                        ? "4px solid #d32f2f"
                        : status === 1
                        ? "4px solid #fbc02d"
                        : "none",
                    transition: "box-shadow 0.3s, background 0.3s",
                  }}
                >
                  {/* Purchase header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="primary"
                      >
                        {t("pharmacy.purchase")} #{purchase.id} -{" "}
                        {purchase.date}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        mt={1}
                      >
                        {t("pharmacy.medicineCompany")}:{" "}
                        {purchase.medicine_company_display}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>
                        <strong>{t("pharmacy.totalAmount")}:</strong>{" "}
                        {purchase.total_amount} ؋
                      </Typography>
                      <Chip
                        label={getApprovalStatusLabel(status)}
                        color={getApprovalStatusColor(status)}
                        sx={{ fontWeight: "bold", minWidth: 80 }}
                      />
                      <Tooltip title={t("pharmacy.editPurchase")}>
                        <IconButton
                          color="primary"
                          onClick={() => handleEditOpen(purchase)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={t("pharmacy.deletePurchase", "Delete Purchase")}
                      >
                        <IconButton
                          color="error"
                          onClick={() => handleDeletePurchase(purchase.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">
                        {t("pharmacy.medicinesInPurchase")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {purchase.items?.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">
                                  {t("pharmacy.medicine")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.quantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.purchasePrice")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.sellPrice")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.expiryDate")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.actions")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {purchase.items.map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell align="center">
                                    {item.medicine_display}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.purchase_price} ؋
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.sell_price} ؋
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.expiry_date}
                                  </TableCell>
                                  <TableCell align="center">
                                    <>
                                      <Tooltip title={t("pharmacy.editItem")}>
                                        <IconButton
                                          color="primary"
                                          onClick={() => handleOpen(item)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t("pharmacy.deleteItem")}>
                                        <IconButton
                                          color="error"
                                          onClick={() =>
                                            handleDeletePurchaseItem(item.id)
                                          }
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>{t("pharmacy.noMedicines")}</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box textAlign="center" mt={5}>
              <Typography variant="h6">
                {t("pharmacy.noMedicineOrder")}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* --- Pagination Controls --- */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("pharmacy.rowsPerPage")}:</Typography>
              <FormControl sx={{ ml: 2 }} size="small">
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPurchases / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>

        {/* --- Edit Dialog --- */}
        <Dialog
          open={editDialogOpen}
          onClose={handleEditClose}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            <Typography fontWeight="bold" color="primary">
              {t("pharmacy.editPurchase")} #{selectedPurchase?.id}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            {selectedPurchase && (
              <PurchaseEdit
                purchase={selectedPurchase}
                onClose={handleEditClose}
                token={token}
                refetchParent={refetch}
              />
            )}
          </DialogContent>
        </Dialog>
      </Grid>
      {/* --- Confirm Delete Dialog --- */}
      <ConfirmDeletePurchaseDialog />
      <ConfirmDeletePurchaseItemDialog />
    </Fragment>
  );
};

export default PurchaseDrugList;
